import React, { useState, useEffect, useCallback, useRef, useMemo, memo, Suspense } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

// Placeholder component for loading state
const Placeholder = () => (
  <div className="w-full h-96 bg-gray-200 rounded-3xl"></div>
);

// CarouselItem component with lazy-loaded iframe
const CarouselItem = memo(({ mediaItem, index, startIndex }) => (
  <div className={`h-96 lg:w-3/5 w-full tamanhoCard flex flex-col m-2 justify-center p-2 mb-4 transition-opacity duration-500 ease-in-out shadow-lg rounded-3xl`}>
    <iframe
      className="h-full w-full rounded-3xl object-cover"
      src={mediaItem.url}
      title={`Slide ${startIndex + index}`}
      allowFullScreen
      loading="lazy" // Ensures iframe is lazy-loaded
    ></iframe>
  </div>
));

const CarouselContainer = () => {
  const [state, setState] = useState({
    visibleImagesCount: 4,
    startIndex: 0,
    isMobile: false,
  });
  const [touchStartX, setTouchStartX] = useState(null);
  const cardsContainer = useRef(null);

  const media = useMemo(() => [
    { url: "https://www.youtube.com/embed/0jBLXVAs6e8", descricao: "um video qualquer", texto: "Revelando as verdades por trás das." },
    { url: "https://www.youtube.com/embed/DukQgyTAEQ8", descricao: "um video qualquer", texto: "Revelando as verdades por trás das." },
    { url: "https://www.youtube.com/embed/U5PR46kVJBA", descricao: "um video qualquer", texto: "Revelando as verdades por trás das." },
    { url: "https://www.youtube.com/embed/Y0a1JCGJLBA", descricao: "um video qualquer", texto: "Revelando as verdades por trás das." },
  ], []);

  // Define updateResponsiveState function
  const updateResponsiveState = useCallback(() => {
    const width = window.innerWidth;
    setState((prevState) => ({
      ...prevState,
      isMobile: width < 768,
      visibleImagesCount: width < 710 ? 1 : width < 1162 ? 3 : 4,
    }));
  }, []);

  // Use ResizeObserver for better resize handling
  useEffect(() => {
    updateResponsiveState(); // Initialize state on mount

    const resizeObserver = new ResizeObserver(() => {
      updateResponsiveState();
    });

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateResponsiveState]);

  const handleTouchStart = (event) => {
    setTouchStartX(event.touches[0].clientX);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null || !cardsContainer.current) return;

    const touchEndX = event.changedTouches[0].clientX;
    const touchDiff = touchStartX - touchEndX;

    if (touchDiff > 50) {
      nextSlide();
    } else if (touchDiff < -50) {
      prevSlide();
    }

    setTouchStartX(null);
  };

  const prevSlide = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      startIndex: (prevState.startIndex - 1 + media.length) % media.length,
    }));
  }, [media.length]);

  const nextSlide = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      startIndex: (prevState.startIndex + 1) % media.length,
    }));
  }, [media.length]);

  const getVisibleMedia = useCallback(() => {
    const end = state.startIndex + state.visibleImagesCount;
    return end <= media.length
      ? media.slice(state.startIndex, end)
      : [
          ...media.slice(state.startIndex),
          ...media.slice(0, end % media.length),
        ];
  }, [state.startIndex, state.visibleImagesCount, media]);

  return (
    <div className="relative">
      <div
        className="flex flex-col fontfamily text-white"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex items-center justify-between">
          {!state.isMobile && (
            <button className="text-white text-2xl hide-on-mobile" onClick={prevSlide}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          )}
          <div ref={cardsContainer} className="flex justify-around w-full mx-2">
            {getVisibleMedia().map((mediaItem, index) => (
              <CarouselItem key={index} mediaItem={mediaItem} index={index} startIndex={state.startIndex} />
            ))}
          </div>
          {!state.isMobile && (
            <button className="text-white text-2xl hide-on-mobile" onClick={nextSlide}>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          )}
        </div>
        <div className="flex justify-center mt-10">
          {media.map((_, index) => (
            <span
              key={index}
              className={`indicator w-3 h-3 mx-1 rounded-full cursor-pointer ${
                state.startIndex <= index && index < state.startIndex + state.visibleImagesCount
                  ? "cor-da-marca-03"
                  : "bg-gray-200"
              }`}
              onClick={() => setState((prevState) => ({ ...prevState, startIndex: index }))}
            ></span>
          ))}
        </div>
      </div>
    </div>
  );
};

// Lazy load CarouselFeedback component
const CarouselFeedbackContainer = () => (
  <Suspense fallback={<Placeholder />}>
    <CarouselContainer />
  </Suspense>
);

export default CarouselFeedbackContainer;
